import * as z from 'zod';
import { EMPLOYMENT_TYPES } from '@/constant/hr/employment-types';
import { canBeDefaultLeave, suggestDefaultLeave } from '@/constant/hr/leave-rule-catalog';
import { dateOnlyOf, toDateOnlyValue } from '@/lib/utils/custom-date/custom-date';
import type { HrTermInput, HrTermRecord, LeaveDetail } from '../types/employee.types';
import type { LeaveCategoryOption } from '../services/leave-lookups.service';
import {
  createScheduleFrom,
  getWorkingInfoDefaults,
  latestScheduleOf,
  workingInfoFromRecord,
  workingInfoSchema,
  workingInfoToPayload,
  workSchedulesRefine,
} from './working-info-schema';

const hrTermSchema = z.object({
  EmploymentType: z.string().min(1, { message: 'ERR_MSG_REQUIRED' }),
  HrPeriodStartDate: z.string().min(1, { message: 'ERR_MSG_REQUIRED' }),
  HrPeriodEndDate: z.string().min(1, { message: 'ERR_MSG_REQUIRED' }),
  IsFlexible: z.boolean(),
});

export type HrTermValues = z.infer<typeof hrTermSchema>;

const hrTermDefaults: HrTermValues = {
  EmploymentType: '',
  HrPeriodStartDate: '',
  HrPeriodEndDate: '',
  IsFlexible: false,
};

/** Legacy stored compact type names ("FullTime"); map those and the display names onto the option list. */
const toEmploymentType = (value?: string | null): string => {
  if (!value) return '';
  const compact = (type: string) => type.replace(/[\s-]/g, '');
  return (
    EMPLOYMENT_TYPES.find((type) => type === value || compact(type) === compact(value)) ?? value
  );
};

const hrTermFromRecord = (record: HrTermRecord): HrTermValues => ({
  EmploymentType: toEmploymentType(record.EmploymentType),
  HrPeriodStartDate: dateOnlyOf(record.HrPeriodStartDate),
  HrPeriodEndDate: dateOnlyOf(record.HrPeriodEndDate),
  IsFlexible: record.IsFlexible ?? false,
});

/**
 * The term's first and last day (both inclusive) are date-only fields: each picked day is stored at
 * 00:00:00 UTC (`YYYY-MM-DDT00:00:00.000Z`, see `toDateOnlyValue`), with no zone conversion.
 */
const hrTermToPayload = (
  values: HrTermValues
): Pick<
  HrTermInput,
  'EmploymentType' | 'HrPeriodStartDate' | 'HrPeriodEndDate' | 'IsFlexible'
> => ({
  EmploymentType: values.EmploymentType,
  HrPeriodStartDate: toDateOnlyValue(values.HrPeriodStartDate) ?? '',
  HrPeriodEndDate: toDateOnlyValue(values.HrPeriodEndDate) ?? '',
  IsFlexible: values.IsFlexible,
});

/** `path` is where the term sits in the form (e.g. `['HrTerms', 1]`), so issues land on that term's fields. */
const hrTermRefine = (
  values: HrTermValues,
  ctx: z.RefinementCtx,
  path: (string | number)[]
): void => {
  // Legacy date pickers capped the start at the end date (and the end at the start date).
  if (
    values.HrPeriodStartDate &&
    values.HrPeriodEndDate &&
    values.HrPeriodStartDate > values.HrPeriodEndDate
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [...path, 'HrPeriodStartDate'],
      message: 'START_DATE_MUST_NOT_BE_AFTER_END_DATE',
    });
  }
};

// --- Leave allotment of a term ---

const requiredNumber = z.number({
  required_error: 'ERR_MSG_REQUIRED',
  invalid_type_error: 'ERR_MSG_REQUIRED',
});

const leaveDetailSchema = z.object({
  LeaveCategoryId: z.string(),
  LeaveCategory: z.string(),
  LeaveLimitTotalDays: requiredNumber.min(0, { message: 'VALID_POSITIVE_NUMBER' }),
  IsApplicable: z.boolean(),
  IsUnlimited: z.boolean(),
  IsDefault: z.boolean(),
});

/** Leave categories of one HR term plus what is carried forward into it (leave days, TOIL days, special TOIL days). */
const leaveAllotmentSchema = z.object({
  LeaveDetails: z.array(leaveDetailSchema),
  LeaveCarryForwardDays: requiredNumber.min(0, { message: 'LEAVE_CARRY_FORWARD_ERROR_MESSAGE' }),
  ToilCarryForwardDays: requiredNumber.min(0, { message: 'TOIL_CARRY_FORWARD_ERROR_MESSAGE' }),
  SpecialToilCarryForwardDays: requiredNumber.min(0, {
    message: 'TOIL_CARRY_FORWARD_ERROR_MESSAGE',
  }),
});

export type LeaveAllotmentValues = z.infer<typeof leaveAllotmentSchema>;

const leaveAllotmentDefaults: LeaveAllotmentValues = {
  LeaveDetails: [],
  LeaveCarryForwardDays: 0,
  ToilCarryForwardDays: 0,
  SpecialToilCarryForwardDays: 0,
};

/**
 * The organization's categories laid over what the term already holds: a category keeps the days and the
 * "applicable" tick the term had for it, a new category starts unticked with its default limit, and a
 * category the organization no longer has drops out.
 */
export const mergeLeaveDetails = (
  categories: LeaveCategoryOption[],
  existing: LeaveDetail[]
): LeaveDetail[] =>
  categories.map((category) => {
    const current = existing.find((detail) => detail.LeaveCategoryId === category.ItemId);
    return {
      LeaveCategoryId: category.ItemId,
      LeaveCategory: category.Title,
      LeaveLimitTotalDays: current?.LeaveLimitTotalDays ?? category.LeaveLimit ?? 0,
      IsApplicable: current?.IsApplicable ?? false,
      IsUnlimited: category.IsUnlimited ?? current?.IsUnlimited ?? false,
      IsDefault: current?.IsDefault ?? false,
    };
  });

export { canBeDefaultLeave };

/**
 * The term's leave rows with exactly one default leave when any category can be one. A valid default
 * the term already has is kept; otherwise (or with `resuggest`, when the employment type changes) the
 * default becomes the suggested one (`suggestDefaultLeave`: intern or probationer casual, else annual,
 * else the first ticked category with a day limit), the same one the leave sheet assumes for a term
 * saved before `IsDefault` existed.
 */
export const withDefaultLeave = (
  details: LeaveDetail[],
  employmentType?: string,
  resuggest = false
): LeaveDetail[] => {
  const current = details.filter((detail) => detail.IsDefault && canBeDefaultLeave(detail));
  let defaultId = !resuggest && current.length === 1 ? current[0].LeaveCategoryId : undefined;
  if (!defaultId) defaultId = suggestDefaultLeave(details, employmentType)?.LeaveCategoryId;
  return details.map((detail) => ({ ...detail, IsDefault: detail.LeaveCategoryId === defaultId }));
};

const leaveAllotmentFromRecord = (record: HrTermRecord): LeaveAllotmentValues => ({
  LeaveDetails: record.LeaveDetails,
  LeaveCarryForwardDays: record.LeaveCarryForwardDays,
  ToilCarryForwardDays: record.ToilCarryForwardDays,
  SpecialToilCarryForwardDays: record.SpecialToilCarryForwardDays,
});

/** Legacy validation: each term needs at least one leave type ticked. */
const hasApplicableLeave = (details: LeaveDetail[]): boolean =>
  details.some((detail) => detail.IsApplicable);

// --- The HR terms of an employee ---

/**
 * One HR term: its period, its working days and its leave allotment. A person has one or more terms and
 * exactly one of them is active (`ActiveTermIndex`).
 */
const hrTermItemSchema = z.object({
  /** Id of the saved `UserHRPeriodicalProfile`; empty for a term that has not been saved yet. */
  ItemId: z.string().optional(),
  ...hrTermSchema.shape,
  ...workingInfoSchema.shape,
  ...leaveAllotmentSchema.shape,
});

export type HrTermItemValues = z.infer<typeof hrTermItemSchema>;

export const hrTermsShape = {
  HrTerms: z.array(hrTermItemSchema).min(1),
  /** Index of the active term, or -1 while none is activated. */
  ActiveTermIndex: z.number().int().min(0, { message: 'PLEASE_ACTIVATE_AT_LEAST_1_HR_TERM' }),
};

export const createEmptyTerm = (): HrTermItemValues => ({
  ItemId: undefined,
  ...hrTermDefaults,
  ...getWorkingInfoDefaults(),
  ...leaveAllotmentDefaults,
});

/** A new term starts from the previous one (legacy "Add another term") except for its dates: its work days
 * are the setting the previous term ends with, as the new term's first (unlocked) setting. */
export const createTermFrom = (previous: HrTermItemValues): HrTermItemValues => {
  const latest = latestScheduleOf(previous.WorkSchedules);
  return {
    ...structuredClone(previous),
    ItemId: undefined,
    HrPeriodStartDate: '',
    HrPeriodEndDate: '',
    WorkSchedules: latest ? [createScheduleFrom(latest)] : getWorkingInfoDefaults().WorkSchedules,
  };
};

const hrTermsDefaults = { HrTerms: [createEmptyTerm()], ActiveTermIndex: -1 };

export const hrTermsFromRecords = (records: HrTermRecord[]) => {
  if (records.length === 0) return hrTermsDefaults;
  return {
    HrTerms: records.map((record): HrTermItemValues => ({
      ItemId: record.ItemId,
      ...hrTermFromRecord(record),
      ...workingInfoFromRecord(record),
      ...leaveAllotmentFromRecord(record),
    })),
    ActiveTermIndex: records.findIndex((record) => record.IsCurrentHrYear),
  };
};

export const hrTermsToPayload = (
  terms: HrTermItemValues[],
  activeTermIndex: number
): HrTermInput[] =>
  terms.map((term, index) => ({
    ItemId: term.ItemId,
    HRPeriod: index + 1,
    IsCurrentHrYear: index === activeTermIndex,
    ...hrTermToPayload(term),
    ...workingInfoToPayload(term),
    LeaveDetails: term.LeaveDetails.map((detail) => ({
      ...detail,
      IsDefault: detail.IsDefault && canBeDefaultLeave(detail),
    })),
    LeaveCarryForwardDays: term.LeaveCarryForwardDays,
    ToilCarryForwardDays: term.ToilCarryForwardDays,
    SpecialToilCarryForwardDays: term.SpecialToilCarryForwardDays,
  }));

export const hrTermsRefine = (terms: HrTermItemValues[], ctx: z.RefinementCtx): void => {
  terms.forEach((term, index) => {
    hrTermRefine(term, ctx, ['HrTerms', index]);
    workSchedulesRefine(term, ctx, ['HrTerms', index]);
    if (!hasApplicableLeave(term.LeaveDetails)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['HrTerms', index, 'LeaveDetails'],
        message: 'LEAVE_TYPE_IS_MANDATORY',
      });
    } else if (
      term.LeaveDetails.some(canBeDefaultLeave) &&
      term.LeaveDetails.filter((detail) => detail.IsDefault && canBeDefaultLeave(detail)).length !==
        1
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['HrTerms', index, 'LeaveDetails'],
        message: 'DEFAULT_LEAVE_IS_MANDATORY',
      });
    }
  });
};

/**
 * Legacy activation rule: with no active term only the first can be activated; afterwards only the term
 * right after the active one can be (the term moves forward one step at a time).
 */
export const canActivateTerm = (index: number, activeTermIndex: number): boolean =>
  activeTermIndex < 0 ? index === 0 : index === activeTermIndex + 1;

/** Terms that already exist in Blocks stay; only terms added in this session can be removed. */
export const canRemoveTerm = (term: HrTermItemValues, termCount: number): boolean =>
  !term.ItemId && termCount > 1;

/** Active index after removing the term at `removedIndex`. */
export const activeIndexAfterRemoval = (activeTermIndex: number, removedIndex: number): number => {
  if (activeTermIndex === removedIndex) return -1;
  return removedIndex < activeTermIndex ? activeTermIndex - 1 : activeTermIndex;
};
